import { useNavigate } from "react-router-dom";
import { Zap, LayoutDashboard, PenTool, Clock, CreditCard, LogOut, Gift, Calendar } from "lucide-react";

export const NavShell = ({ user, onLogout, active, children }) => {
  const navigate = useNavigate();

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { id: "generate", label: "Generate", icon: PenTool, path: "/generate" },
    { id: "calendar", label: "Calendar", icon: Calendar, path: "/calendar" },
    { id: "history", label: "History", icon: Clock, path: "/history" },
    { id: "pricing", label: "Credits", icon: CreditCard, path: "/pricing" },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/5 bg-gray-950/50 hidden md:flex flex-col" data-testid="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")} data-testid="sidebar-logo">
            <Zap className="w-6 h-6 text-violet-400" />
            <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Outfit' }}>ContentForge</span>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors duration-200 ${active === item.id ? 'bg-violet-500/10 text-violet-300 border border-violet-500/20' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-sm font-semibold text-violet-300">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-violet-500/5 border border-violet-500/10 mb-3">
            <span className="text-xs text-gray-400">Credits</span>
            <span className="text-sm font-semibold text-violet-300">{user?.credits ?? 0}</span>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/5 rounded-lg transition-colors duration-200" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-violet-400" />
            <span className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>ContentForge</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-violet-300 font-medium">{user?.credits ?? 0} credits</span>
            <button onClick={onLogout} className="text-gray-400 hover:text-red-400 p-1" data-testid="mobile-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex border-t border-white/5">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-xs transition-colors duration-200 ${active === item.id ? 'text-violet-400' : 'text-gray-500'}`}
              data-testid={`mobile-nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-auto md:p-0 pt-28 md:pt-0">
        {children}
      </main>
    </div>
  );
};
